import DeleteIcon from "@mui/icons-material/DeleteOutlineRounded";
import PublishIcon from "@mui/icons-material/LanguageRounded";
import SaveIcon from "@mui/icons-material/SaveAltRounded";
import { Button } from "@mui/material";
import type { GridApiPremium } from "@mui/x-data-grid-premium";
import { gridRowSelector } from "@mui/x-data-grid-premium";
import type { RefObject } from "react";
import { useMemo } from "react";
import type { DocumentCollectionOperations } from "../../../../api/types/documentCollectionTypes";
import type { FundStructureDocument } from "../../../../api/types/fundStructureTypes";
import UnpublishIcon from "@entrilia/shared/icons/UnpublishIcon";

interface Props {
  selectedIds: string[];
  allowStatusChanges: boolean;
  disabled?: boolean;
  onDownload: () => void;
  onChangeStatus: (operation: DocumentCollectionOperations) => void;
  apiRef: RefObject<GridApiPremium | null>;
}

const getAvailableOperations = (selectedIds: string[], apiRef: RefObject<GridApiPremium | null>) => {
  let canPublish = true;
  let canUnpublish = true;
  let canDelete = true;

  for (const id of selectedIds) {
    const document = gridRowSelector(apiRef, id) as FundStructureDocument | undefined;
    if (document) {
      canPublish = canPublish && document.messageSource === "DataImport" && document.status === "Preview";
      canUnpublish = canUnpublish && document.messageSource === "DataImport" && document.status === "Live";
      canDelete =
        canDelete &&
        document.messageSource === "DataImport" &&
        (document.status === "Live" || document.status === "Preview");
    }
  }

  return { canPublish, canUnpublish, canDelete };
};

const FundStructureDocumentsGridToolbar = ({
  selectedIds,
  allowStatusChanges,
  disabled,
  onDownload,
  onChangeStatus,
  apiRef,
}: Props) => {
  const availableOperations = useMemo(() => getAvailableOperations(selectedIds, apiRef), [apiRef, selectedIds]);

  return (
    <>
      <Button disabled={disabled} onClick={onDownload} color="secondary" variant="text" startIcon={<SaveIcon />}>
        Download
      </Button>

      {allowStatusChanges && (
        <Button
          disabled={!availableOperations.canPublish}
          variant="text"
          color="secondary"
          startIcon={<PublishIcon />}
          onClick={() => onChangeStatus("MakeLive")}
        >
          Publish
        </Button>
      )}

      {allowStatusChanges && (
        <Button
          disabled={!availableOperations.canUnpublish}
          variant="text"
          color="secondary"
          startIcon={<UnpublishIcon />}
          onClick={() => onChangeStatus("MakePreview")}
        >
          Unpublish
        </Button>
      )}

      {allowStatusChanges && (
        <Button
          disabled={!availableOperations.canDelete}
          variant="text"
          color="error"
          startIcon={<DeleteIcon />}
          onClick={() => onChangeStatus("Revoke")}
        >
          Delete
        </Button>
      )}
    </>
  );
};

export default FundStructureDocumentsGridToolbar;
